import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { getApiErrorMessage } from "./helpers/apiErrorMessage";

function OtherProfile() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [favouriteAlbums, setFavouriteAlbums] = useState([]);
    const [diaryEntries, setDiaryEntries] = useState([]);
    const [diaryTotalCount, setDiaryTotalCount] = useState(0);
    const [wishlistEntries, setWishlistEntries] = useState([]);
    const [lists, setLists] = useState([]);
    const [followers, setFollowers] = useState([]);
    const [following, setFollowing] = useState([]);
    const [listEntriesById, setListEntriesById] = useState({});
    const [openListId, setOpenListId] = useState(null);
    const [privateSections, setPrivateSections] = useState({
        diary: false,
        wishlist: false,
        friends: false,
        statistics: false
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadUserPageData();
    }, [userId]);

    const loadUserPageData = async () => {
        setLoading(true);
        setError(null);
        setPrivateSections({ diary: false, wishlist: false, friends: false, statistics: false });
        try {
            const authRes = await axios.get('/api/auth/check-login');
            if (authRes.data?.loggedIn && String(authRes.data.userId) === String(userId)) {
                navigate('/profile', { replace: true });
                return;
            }

            const [profileRes, favRes, diaryRes, wishlistRes, listsRes, followersRes, followingRes] = await Promise.allSettled([
                axios.get(`/api/users/${userId}`),
                axios.get(`/api/users/${userId}/favourite-albums`),
                axios.get(`/api/users/${userId}/diary`),
                axios.get(`/api/users/${userId}/wishlist`),
                axios.get(`/api/users/${userId}/lists`),
                axios.get(`/api/users/${userId}/followers`),
                axios.get(`/api/users/${userId}/following`)
            ]);

            if (profileRes.status === 'rejected') {
                throw profileRes.reason;
            }

            setUser(profileRes.value.data);

            const sectionErrors = [];

            if (favRes.status === 'fulfilled') {
                setFavouriteAlbums(favRes.value.data);
            } else if (favRes.reason?.response?.status === 403) {
                setFavouriteAlbums([]);
                setPrivateSections((prev) => ({ ...prev, statistics: true }));
            } else {
                sectionErrors.push(getApiErrorMessage(favRes.reason, 'load favourite albums'));
            }

            if (diaryRes.status === 'fulfilled') {
                setDiaryEntries(diaryRes.value.data.entries);
                setDiaryTotalCount(diaryRes.value.data.totalEntries);
            } else if (diaryRes.reason?.response?.status === 403) {
                setDiaryEntries([]);
                setDiaryTotalCount(0);
                setPrivateSections((prev) => ({ ...prev, diary: true }));
            } else {
                sectionErrors.push(getApiErrorMessage(diaryRes.reason, 'load diary'));
            }

            if (wishlistRes.status === 'fulfilled') {
                setWishlistEntries(wishlistRes.value.data);
            } else if (wishlistRes.reason?.response?.status === 403) {
                setWishlistEntries([]);
                setPrivateSections((prev) => ({ ...prev, wishlist: true }));
            } else {
                sectionErrors.push(getApiErrorMessage(wishlistRes.reason, 'load wishlist'));
            }

            if (listsRes.status === 'fulfilled') {
                setLists(listsRes.value.data);
            } else {
                setLists([]);
                sectionErrors.push(getApiErrorMessage(listsRes.reason, 'load lists'));
            }

            const followersPrivate = followersRes.status === 'rejected' && followersRes.reason?.response?.status === 403;
            const followingPrivate = followingRes.status === 'rejected' && followingRes.reason?.response?.status === 403;

            if (followersPrivate || followingPrivate) {
                setFollowers([]);
                setFollowing([]);
                setPrivateSections((prev) => ({ ...prev, friends: true }));
            } else {
                if (followersRes.status === 'fulfilled') {
                    setFollowers(followersRes.value.data);
                } else {
                    setFollowers([]);
                    sectionErrors.push(getApiErrorMessage(followersRes.reason, 'load followers'));
                }

                if (followingRes.status === 'fulfilled') {
                    setFollowing(followingRes.value.data);
                } else {
                    setFollowing([]);
                    sectionErrors.push(getApiErrorMessage(followingRes.reason, 'load following'));
                }
            }

            if (sectionErrors.length > 0) {
                setError(sectionErrors[0]);
            }
        } catch (error) {
            setError(getApiErrorMessage(error, 'load this profile'));
            console.error('Error fetching other user page data:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleListEntries = async (listId) => {
        if (openListId === listId) {
            setOpenListId(null);
            return;
        }

        setOpenListId(listId);
        if (listEntriesById[listId]) {
            return;
        }

        try {
            const res = await axios.get(`/api/users/${userId}/lists/${listId}`);
            setListEntriesById((prev) => ({
                ...prev,
                [listId]: res.data
            }));
        } catch (error) {
            setError(getApiErrorMessage(error, 'load list entries'));
            console.error('Error fetching list entries:', error);
        }
    };

    const handleFollow = async () => {
        try {
            await axios.post(`/api/auth/follow/${userId}`);
            const res = await axios.get(`/api/users/${userId}`);
            setUser(res.data);
        } catch (error) {
            setError(getApiErrorMessage(error, 'follow this user'));
            console.error('Error following user:', error);
        }
    };

    const handleUnfollow = async () => {
        try {
            await axios.post(`/api/auth/unfollow/${userId}`);
            const res = await axios.get(`/api/users/${userId}`);
            setUser(res.data);
        } catch (error) {
            setError(getApiErrorMessage(error, 'unfollow this user'));
            console.error('Error unfollowing user:', error);
        }
    };

    if (loading) {
        return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">Loading...</div>;
    }

    if (!user) {
        return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">User not found</div>;
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white px-6 pb-10">
            {error && (
                <div className="bg-red-900 border-l-4 border-red-600 p-4 m-4 rounded">
                    <div className="flex justify-between items-center">
                        <p className="text-red-200">{error}</p>
                        <button onClick={() => setError(null)} className="text-red-400 hover:text-red-200 text-2xl">×</button>
                    </div>
                </div>
            )}
            <div className="max-w-2xl mx-auto">
                <button
                    onClick={() => navigate(-1)}
                    className="text-gray-400 hover:text-white mb-6 flex items-center gap-2 transition"
                >
                    <i className="fas fa-arrow-left"></i> Back
                </button>

                <h1 className="text-4xl font-bold mb-10">{user.username}</h1>

                {/* Profile Card */}
                <div className="bg-gray-800 rounded-lg p-8 mb-8">
                    {/* Avatar Section */}
                    <div className="flex items-center gap-6 mb-8">
                        <div className="w-24 h-24 bg-green-600 rounded-full flex items-center justify-center text-4xl font-bold">
                            {user.username.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold">{user.username}</h2>
                            {!privateSections.friends && (
                                <div className="mt-2 space-y-1 text-gray-400">
                                    <button
                                        onClick={() => navigate(`/profile/${userId}/followers`)}
                                        className="block hover:text-white transition"
                                    >
                                        <span className="text-green-400 font-bold">{user.followersCount}</span> Followers
                                    </button>
                                    <button
                                        onClick={() => navigate(`/profile/${userId}/following`)}
                                        className="block hover:text-white transition"
                                    >
                                        <span className="text-green-400 font-bold">{user.followingCount}</span> Following
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4">
                        {user.isFollowing ? (
                            <button
                                onClick={handleUnfollow}
                                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded transition"
                            >
                                Unfollow
                            </button>
                        ) : (
                            <button
                                onClick={handleFollow}
                                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded transition"
                            >
                                Follow
                            </button>
                        )}
                    </div>
                </div>

                {/* Favourite Albums */}
                <div className="bg-gray-800 rounded-lg p-8 mb-8">
                    <h3 className="text-xl font-bold mb-6">Favourite Albums</h3>
                        <div className="grid grid-cols-4 gap-4">
                            {[1, 2, 3, 4].map((slot) => {
                                const album = favouriteAlbums.find(a => a.position === slot);
                                return album ? (
                                    <div key={slot} className="relative group">
                                        <div className="aspect-square bg-gray-700 rounded-lg overflow-hidden">
                                            {album.albumCover ? (
                                                <img src={album.albumCover} alt={album.title} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs text-center p-2">
                                                    {album.title}
                                                </div>
                                            )}
                                        </div>
                                        <div className="mt-2">
                                            <p className="text-white text-xs font-semibold truncate">{album.title}</p>
                                            <p className="text-gray-400 text-xs truncate">{album.artist}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        key={slot}
                                        className="aspect-square bg-gray-700 rounded-lg flex flex-col items-center justify-center text-gray-500 border-2 border-dashed border-gray-600"
                                    >
                                        <span className="text-2xl">-</span>
                                    </div>
                                );
                            })}
                        </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-800 rounded-lg p-8 mb-8">
                    {privateSections.statistics ? (
                        <p className="text-gray-400">This user has hidden statistics.</p>
                    ) : (
                        <div>
                            <h3 className="text-xl font-bold mb-6">Public Activity</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-gray-700 p-4 rounded-lg text-center">
                                    <div className="text-2xl font-bold text-green-400">{privateSections.diary ? 'Hidden' : diaryTotalCount}</div>
                                    <div className="text-sm text-gray-300">Diary Entries</div>
                                </div>
                                <div className="bg-gray-700 p-4 rounded-lg text-center">
                                    <div className="text-2xl font-bold text-green-400">{privateSections.wishlist ? 'Hidden' : wishlistEntries.length}</div>
                                    <div className="text-sm text-gray-300">Wishlist</div>
                                </div>
                                <div className="bg-gray-700 p-4 rounded-lg text-center">
                                    <div className="text-2xl font-bold text-green-400">{lists.length}</div>
                                    <div className="text-sm text-gray-300">Lists</div>
                                </div>
                                <div className="bg-gray-700 p-4 rounded-lg text-center">
                                    <div className="text-2xl font-bold text-green-400">{favouriteAlbums.length}</div>
                                    <div className="text-sm text-gray-300">Favourites</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Network */}
                <div className="bg-gray-800 rounded-lg p-8 mb-8">
                    <div className="flex items-center justify-between mb-6 gap-3">
                        <h3 className="text-xl font-bold">Network</h3>
                    </div>
                    {privateSections.friends ? (
                        <p className="text-gray-400">This user has hidden followers and following.</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-gray-700 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-semibold text-white">Followers</h4>
                                    <button
                                        onClick={() => navigate(`/profile/${userId}/followers`)}
                                        className="text-xs bg-gray-600 hover:bg-gray-500 px-3 py-1 rounded"
                                    >
                                        View All
                                    </button>
                                </div>
                                {followers.length === 0 ? (
                                    <p className="text-gray-400 text-sm">No followers yet.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {followers.slice(0, 5).map((follower) => (
                                            <button
                                                key={follower.userId}
                                                onClick={() => navigate(`/profile/${follower.userId}`)}
                                                className="w-full text-left bg-gray-800 hover:bg-gray-600 transition rounded p-2 text-sm"
                                            >
                                                {follower.username}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="bg-gray-700 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-semibold text-white">Following</h4>
                                    <button
                                        onClick={() => navigate(`/profile/${userId}/following`)}
                                        className="text-xs bg-gray-600 hover:bg-gray-500 px-3 py-1 rounded"
                                    >
                                        View All
                                    </button>
                                </div>
                                {following.length === 0 ? (
                                    <p className="text-gray-400 text-sm">Not following anyone yet.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {following.slice(0, 5).map((followedUser) => (
                                            <button
                                                key={followedUser.userId}
                                                onClick={() => navigate(`/profile/${followedUser.userId}`)}
                                                className="w-full text-left bg-gray-800 hover:bg-gray-600 transition rounded p-2 text-sm"
                                            >
                                                {followedUser.username}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Diary */}
                <div className="bg-gray-800 rounded-lg p-8 mb-8">
                    <div className="flex items-center justify-between mb-6 gap-3">
                        <h3 className="text-xl font-bold">Recent Diary</h3>
                        {!privateSections.diary && (
                            <button
                                onClick={() => navigate(`/profile/${userId}/diary`)}
                                className="bg-gray-700 hover:bg-gray-600 text-sm px-4 py-2 rounded transition"
                            >
                                View Full Diary
                            </button>
                        )}
                    </div>
                    {privateSections.diary ? (
                        <p className="text-gray-400">This user has hidden their diary.</p>
                    ) : diaryEntries.length === 0 ? (
                        <p className="text-gray-400">No diary entries yet.</p>
                    ) : (
                        <div className="space-y-4">
                            {diaryEntries.slice(0, 5).map((entry) => (
                                <div key={entry.entryId} className="bg-gray-700 p-4 rounded-lg flex gap-4 items-center">
                                    <img
                                        src={entry.album.albumCover || 'https://placehold.co/75x75'}
                                        alt={entry.album.title}
                                        className="w-12 h-12 rounded-lg object-cover"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-white font-semibold truncate">{entry.album.title}</p>
                                        <p className="text-gray-400 text-sm truncate">{entry.album.artist}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-gray-300 text-sm">{entry.addedOn}</p>
                                        {entry.rating > 0 && <p className="text-green-400 text-sm">Rating: {entry.rating}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Wishlist */}
                <div className="bg-gray-800 rounded-lg p-8 mb-8">
                    <div className="flex items-center justify-between mb-6 gap-3">
                        <h3 className="text-xl font-bold">Wishlist</h3>
                        {!privateSections.wishlist && (
                            <button
                                onClick={() => navigate(`/profile/${userId}/wishlist`)}
                                className="bg-gray-700 hover:bg-gray-600 text-sm px-4 py-2 rounded transition"
                            >
                                View Full Wishlist
                            </button>
                        )}
                    </div>
                    {privateSections.wishlist ? (
                        <p className="text-gray-400">This user has hidden their wishlist.</p>
                    ) : wishlistEntries.length === 0 ? (
                        <p className="text-gray-400">No wishlist entries yet.</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {wishlistEntries.slice(0, 8).map((entry) => (
                                <div key={entry.listEntryId} className="bg-gray-700 p-4 rounded-lg flex gap-3 items-center">
                                    <img
                                        src={entry.album.albumCover || 'https://placehold.co/75x75'}
                                        alt={entry.album.title}
                                        className="w-10 h-10 rounded object-cover"
                                    />
                                    <div className="min-w-0">
                                        <p className="text-white text-sm font-semibold truncate">{entry.album.title}</p>
                                        <p className="text-gray-400 text-xs truncate">{entry.album.artist}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Lists */}
                <div className="bg-gray-800 rounded-lg p-8 mb-8">
                    <div className="flex items-center justify-between mb-6 gap-3">
                        <h3 className="text-xl font-bold">Lists</h3>
                        <button
                            onClick={() => navigate(`/profile/${userId}/lists`)}
                            className="bg-gray-700 hover:bg-gray-600 text-sm px-4 py-2 rounded transition"
                        >
                            View All Lists
                        </button>
                    </div>
                    {lists.length === 0 ? (
                        <p className="text-gray-400">No lists yet.</p>
                    ) : (
                        <div className="space-y-4">
                            {lists.slice(0, 5).map((list) => (
                                <div key={list.listId} className="bg-gray-700 rounded-lg overflow-hidden">
                                    <button
                                        onClick={() => toggleListEntries(list.listId)}
                                        className="w-full p-4 text-left hover:bg-gray-600 transition flex items-center justify-between"
                                    >
                                        <div className="flex items-center gap-4 min-w-0">
                                            <div className="relative w-[100px] h-[48px] flex-shrink-0">
                                                <img
                                                    className="absolute left-0 top-0 w-12 h-12 rounded-lg border border-gray-700 z-30"
                                                    src={list.covers?.c1 || 'https://placehold.co/75x75'}
                                                    alt="Top cover"
                                                />
                                                <img
                                                    className="absolute left-4 top-0 w-12 h-12 rounded-lg border border-gray-700 z-20"
                                                    src={list.covers?.c2 || 'https://placehold.co/75x75'}
                                                    alt="Middle cover"
                                                />
                                                <img
                                                    className="absolute left-8 top-0 w-12 h-12 rounded-lg border border-gray-700 z-10"
                                                    src={list.covers?.c3 || 'https://placehold.co/75x75'}
                                                    alt="Bottom cover"
                                                />
                                            </div>
                                            <div className="min-w-0">
                                            <p className="text-white font-semibold">{list.title}</p>
                                            <p className="text-gray-400 text-sm">{list.entriesCount} entries • {list.createdAt}</p>
                                            </div>
                                        </div>
                                        <span className="text-gray-300">{openListId === list.listId ? 'Hide' : 'View'}</span>
                                    </button>

                                    {openListId === list.listId && (
                                        <div className="border-t border-gray-600 p-4 space-y-3">
                                            <div className="flex justify-end">
                                                <button
                                                    onClick={() => navigate(`/profile/${userId}/lists/${list.listId}`)}
                                                    className="bg-gray-600 hover:bg-gray-500 text-white text-xs px-3 py-1 rounded transition"
                                                >
                                                    Open Full List
                                                </button>
                                            </div>
                                            {!listEntriesById[list.listId] ? (
                                                <p className="text-gray-400 text-sm">Loading entries...</p>
                                            ) : listEntriesById[list.listId].length === 0 ? (
                                                <p className="text-gray-400 text-sm">This list has no entries yet.</p>
                                            ) : (
                                                listEntriesById[list.listId].slice(0, 5).map((entry) => (
                                                    <div key={entry.listEntryId} className="flex items-center gap-3 bg-gray-800 p-3 rounded">
                                                        <img
                                                            src={entry.album.albumCover || 'https://placehold.co/75x75'}
                                                            alt={entry.album.title}
                                                            className="w-10 h-10 rounded object-cover"
                                                        />
                                                        <div className="min-w-0">
                                                            <p className="text-white text-sm font-semibold truncate">{entry.album.title}</p>
                                                            <p className="text-gray-400 text-xs truncate">{entry.album.artist}</p>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default OtherProfile;
